import { useQuery } from '@tanstack/react-query';
import { darkTheme } from '../../../lib/tokens';
import { fetchUserLearning } from './api';
import { LearningHero } from './LearningHero';
import { EngagementHeatmap } from './EngagementHeatmap';
import { TopicMasterySection } from './TopicMasterySection';
import { QuizTrendChart } from './QuizTrendChart';
import { TutorCostSection } from './TutorCostSection';
import { AchievementsSection } from './AchievementsSection';

interface Props {
  userId: string;
  app: string;
  enabled: boolean;
}

// Lazy: nur fetchen wenn der Tab tatsaechlich aktiv ist (siehe Spec §4.2).
// React Query cached fuer 5 Minuten — Reload ueber Tab-Switch ist billig.
// `app` aus dem globalen Admin-Picker scopt die Lern-Aggregate pro Lern-App.

export function LearningTab({ userId, app, enabled }: Props): JSX.Element {
  const { data, isLoading, error } = useQuery({
    queryKey: ['user-learning', userId, app],
    queryFn: () => fetchUserLearning(userId, app),
    enabled,
    staleTime: 5 * 60_000,
  });

  if (isLoading) return <LoadingSkeleton />;
  if (error) {
    return (
      <div
        className="rounded-md p-4 text-sm"
        style={{ background: darkTheme.colors.dangerBg, color: darkTheme.colors.danger }}
      >
        Fehler beim Laden: {error instanceof Error ? error.message : 'unbekannt'}
      </div>
    );
  }
  if (!data) return <></>;

  const isEmpty =
    data.lessons.completed === 0 &&
    data.sessions.totalElapsedSeconds === 0 &&
    data.quiz.total === 0 &&
    data.tutorUsage.totals.callCount === 0;

  if (isEmpty) {
    return <LearningHero data={data} />; // Hero zeigt Persona/Streak; darunter Hint.
  }

  return (
    <div className="space-y-4">
      <LearningHero data={data} />
      <EngagementHeatmap daily={data.daily} />
      <div className="grid gap-4 lg:grid-cols-2">
        <TopicMasterySection items={data.topicMastery} />
        <QuizTrendChart
          weekly={data.weeklyQuizScores}
          summary={data.quiz}
          weeks={data.meta.weeklyScoreWeeks}
        />
      </div>
      <TutorCostSection
        tutor={data.tutorUsage}
        voice={data.voiceSessions}
        dailyDays={data.meta.tutorDailyDays}
      />
      <AchievementsSection achievements={data.achievements} />
    </div>
  );
}

function LoadingSkeleton(): JSX.Element {
  return (
    <div className="space-y-4">
      <div className="h-24 animate-pulse rounded-md" style={{ background: darkTheme.colors.bgSurface }} />
      <div className="h-40 animate-pulse rounded-md" style={{ background: darkTheme.colors.bgSurface }} />
      <div className="grid gap-4 lg:grid-cols-2">
        <div
          className="h-64 animate-pulse rounded-md"
          style={{ background: darkTheme.colors.bgSurface }}
        />
        <div
          className="h-64 animate-pulse rounded-md"
          style={{ background: darkTheme.colors.bgSurface }}
        />
      </div>
      <div className="h-72 animate-pulse rounded-md" style={{ background: darkTheme.colors.bgSurface }} />
    </div>
  );
}
